import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaTrashAlt, FaEdit, FaSave, FaTimes, FaPlus, FaUser } from "react-icons/fa";
import "./UserManagement.css";

export default function UserManagement({ onClose }) {
  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newUser, setNewUser] = useState({ first_name: "", last_name: "", email: "", password: "", role: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/users");
      const data = await res.json();

      const usersArray = (Array.isArray(data) ? data : data.recordset || []).map(u => ({
        id: u.user_id,
        first_name: u.first_name,
        last_name: u.last_name,
        email: u.email,
        role: u.role,
      }));

      setUsers(usersArray);
    } catch (err) {
      console.error("Failed to fetch users", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (id, field, value) => {
    const updated = users.map((u) =>
      u.id === id ? { ...u, [field]: value } : u
    );
    setUsers(updated);
  };

  const saveEdit = async (user) => {
    try {
      await fetch(`https://dshb-deposits.its.uiowa.edu/api/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role,
        }),
      });

      setEditingId(null);
      fetchUsers();
      toast.success("User updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await fetch(`https://dshb-deposits.its.uiowa.edu/api/users/${id}`, {
          method: "DELETE",
        });

        fetchUsers();
        toast.success("User deleted successfully");
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete user");
      }
    }
  };

  const handleAddUser = async () => {
    if (!newUser.first_name || !newUser.last_name || !newUser.email || !newUser.password || !newUser.role) {
      return toast.error("All fields are required");
    }

    try {
      await fetch("https://dshb-deposits.its.uiowa.edu/api/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newUser),
      });

      setShowAdd(false);
      setNewUser({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        role: ""
      });

      fetchUsers();
      toast.success("User added successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to add user");
    }
  };

  const adminUsers = users.filter((u) => u.role === "admin");

  return (
    <div className="user-management-modal-overlay">
      <div className="user-management-modal">
        {/* HEADER */}
        <div className="user-management-header">
          <div className="user-management-title">
            <FaUser className="user-management-icon" />
            <div>
              <h2>User Management</h2>
              <p>Manage admin users and their permissions</p>
            </div>
          </div>
          <button className="user-management-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* CONTENT */}
        <div className="user-management-content">
          {/* TOOLBAR */}
          <div className="user-management-toolbar">
            <div className="user-management-stats">
              <span className="user-count">Total Admins: <strong>{adminUsers.length}</strong></span>
            </div>
            <button className="user-management-add-btn" onClick={() => setShowAdd(true)}>
              <FaPlus /> Add New User
            </button>
          </div>

          {/* TABLE — becomes stacked cards on mobile via CSS (see
              .user-management-table responsive rules using data-label) */}
          {loading ? (
            <div className="user-management-loading">Loading users...</div>
          ) : adminUsers.length === 0 ? (
            <div className="user-management-empty">
              <FaUser className="empty-icon" />
              <p>No admin users found</p>
            </div>
          ) : (
            <div className="user-management-table-wrapper">
              <table className="user-management-table">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map((u) => (
                    <tr key={u.id} className={`user-row ${editingId === u.id ? "editing" : ""}`}>
                      {/* FIRST NAME */}
                      <td className="table-cell" data-label="First Name">
                        {editingId === u.id ? (
                          <input
                            type="text"
                            className="table-input"
                            value={u.first_name}
                            onChange={(e) =>
                              handleEditUser(u.id, "first_name", e.target.value)
                            }
                          />
                        ) : (
                          u.first_name
                        )}
                      </td>

                      {/* LAST NAME */}
                      <td className="table-cell" data-label="Last Name">
                        {editingId === u.id ? (
                          <input
                            type="text"
                            className="table-input"
                            value={u.last_name}
                            onChange={(e) =>
                              handleEditUser(u.id, "last_name", e.target.value)
                            }
                          />
                        ) : (
                          u.last_name
                        )}
                      </td>

                      {/* EMAIL */}
                      <td className="table-cell" data-label="Email">
                        {editingId === u.id ? (
                          <input
                            type="email"
                            className="table-input"
                            value={u.email}
                            onChange={(e) =>
                              handleEditUser(u.id, "email", e.target.value)
                            }
                          />
                        ) : (
                          u.email
                        )}
                      </td>

                      {/* ROLE */}
                      <td className="table-cell" data-label="Role">
                        {editingId === u.id ? (
                          <select
                            className="table-select"
                            value={u.role}
                            onChange={(e) =>
                              handleEditUser(u.id, "role", e.target.value)
                            }
                          >
                            <option value="admin">Admin</option>
                            <option value="depositor">Depositor</option>
                          </select>
                        ) : (
                          <span className="role-badge">{u.role}</span>
                        )}
                      </td>

                      {/* ACTIONS */}
                      <td className="table-cell actions-cell" data-label="Actions">
                        {editingId === u.id ? (
                          <>
                            <button
                              className="action-btn save-btn"
                              onClick={() => saveEdit(u)}
                              title="Save changes"
                            >
                              <FaSave />
                            </button>
                            <button
                              className="action-btn cancel-btn"
                              onClick={() => setEditingId(null)}
                              title="Cancel"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="action-btn edit-btn"
                              onClick={() => setEditingId(u.id)}
                              title="Edit user"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteUser(u.id)}
                              title="Delete user"
                            >
                              <FaTrashAlt />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ADD USER MODAL */}
        {showAdd && (
          <div className="add-user-modal-overlay">
            <div className="add-user-modal">
              <div className="add-user-header">
                <h3>Add New User</h3>
                <button className="add-user-close" onClick={() => setShowAdd(false)}>
                  <FaTimes />
                </button>
              </div>

              <div className="add-user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name <span className="required">*</span></label>
                    <input
                      id="first_name"
                      type="text"
                      placeholder="Enter first name"
                      className="form-input"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="last_name">Last Name <span className="required">*</span></label>
                    <input
                      id="last_name"
                      type="text"
                      placeholder="Enter last name"
                      className="form-input"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email <span className="required">*</span></label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    className="form-input"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password <span className="required">*</span></label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    className="form-input"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Role <span className="required">*</span></label>
                  <select
                    id="role"
                    className="form-select"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="">Select a role</option>
                    <option value="admin">Admin</option>
                    <option value="depositor">Depositor</option>
                  </select>
                </div>
              </div>

              <div className="add-user-actions">
                <button className="btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn-save" onClick={handleAddUser}>Save User</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}